import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Card, 
  CardContent, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  Alert, 
  Box,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Menu,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Phone as PhoneIcon,
  MoreVert as MoreVertIcon,
  CloudDownload as ImportIcon
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { phoneNumberService } from '../services/api';
import TwilioImportDialog from '../components/TwilioImportDialog';

const PhoneNumbersPage = () => {
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingNumber, setEditingNumber] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedNumberForMenu, setSelectedNumberForMenu] = useState(null);
  const [showTwilioImport, setShowTwilioImport] = useState(false);
  const [formData, setFormData] = useState({
    phoneNumber: '',
    friendlyName: '',
    capabilities: {
      voice: true,
      sms: false,
      mms: false
    },
    notes: '',
    monthlyCost: 0
  });

  useEffect(() => {
    fetchPhoneNumbers();
  }, []);

  const fetchPhoneNumbers = async () => {
    try {
      const data = await phoneNumberService.getPhoneNumbers();
      console.log('Phone numbers response:', data);
      
      // Handle different response structures
      let numbersArray;
      if (data.phoneNumbers) {
        numbersArray = data.phoneNumbers;
      } else if (Array.isArray(data)) {
        numbersArray = data;
      } else if (data.data && Array.isArray(data.data)) {
        numbersArray = data.data;
      } else {
        console.error('Unexpected phone numbers data structure:', data);
        numbersArray = [];
      }
      
      setPhoneNumbers(numbersArray);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching phone numbers:', error);
      setError('Failed to load phone numbers. Please try again.');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingNumber) {
        await phoneNumberService.updatePhoneNumber(editingNumber._id, formData);
      } else {
        await phoneNumberService.createPhoneNumber(formData);
      }
      
      setShowModal(false);
      setEditingNumber(null);
      resetForm();
      fetchPhoneNumbers();
    } catch (error) {
      console.error('Error saving phone number:', error);
      setError(error.response?.data?.message || 'Failed to save phone number');
    }
  };

  const handleEdit = (phoneNumber) => {
    setEditingNumber(phoneNumber);
    setFormData({
      phoneNumber: phoneNumber.phoneNumber,
      friendlyName: phoneNumber.friendlyName,
      capabilities: phoneNumber.capabilities,
      notes: phoneNumber.notes || '',
      monthlyCost: phoneNumber.monthlyCost || 0
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this phone number?')) {
      try {
        await phoneNumberService.deletePhoneNumber(id);
        fetchPhoneNumbers();
      } catch (error) {
        console.error('Error deleting phone number:', error);
        setError('Failed to delete phone number');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      phoneNumber: '',
      friendlyName: '',
      capabilities: {
        voice: true,
        sms: false,
        mms: false
      },
      notes: '',
      monthlyCost: 0
    });
  };

  const getStatusChip = (status) => {
    const colorMap = {
      active: 'success',
      inactive: 'default',
      pending: 'warning'
    };
    return <Chip label={status} color={colorMap[status] || 'default'} size="small" />;
  };

  const getCapabilityChips = (capabilities) => {
    const chips = [];
    if (capabilities.voice) chips.push(<Chip key="voice" label="Voice" color="primary" size="small" sx={{ mr: 0.5 }} />);
    if (capabilities.sms) chips.push(<Chip key="sms" label="SMS" color="info" size="small" sx={{ mr: 0.5 }} />);
    if (capabilities.mms) chips.push(<Chip key="mms" label="MMS" color="warning" size="small" sx={{ mr: 0.5 }} />);
    return chips;
  };

  const handleMenuOpen = (event, number) => {
    setAnchorEl(event.currentTarget);
    setSelectedNumberForMenu(number);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedNumberForMenu(null);
  };

  const handleTwilioImportSuccess = (importedNumber) => {
    fetchPhoneNumbers();
    setShowTwilioImport(false);
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Phone Numbers
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your organization's phone numbers
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingNumber(null);
              resetForm();
              setShowModal(true);
            }}
          >
            Add Phone Number
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<ImportIcon />}
            onClick={() => setShowTwilioImport(true)}
          >
            Import from Twilio
          </Button>
          <Button 
            variant="outlined" 
            component={Link}
            to="/dashboard"
          >
            Back to Dashboard
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {phoneNumbers.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <PhoneIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No phone numbers found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Add your first phone number to start making calls with your AI agents.
            </Typography>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />}
              onClick={() => {
                setEditingNumber(null);
                resetForm();
                setShowModal(true);
              }}
            >
              Add Your First Phone Number
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Phone Number</strong></TableCell>
                  <TableCell><strong>Friendly Name</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Capabilities</strong></TableCell>
                  <TableCell><strong>Monthly Cost</strong></TableCell>
                  <TableCell><strong>Provider</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {phoneNumbers.map((number) => (
                  <TableRow key={number._id}>
                    <TableCell>
                      <Typography variant="body1" fontWeight="medium">
                        {number.phoneNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {number.friendlyName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {getStatusChip(number.status)}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {getCapabilityChips(number.capabilities)}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        ${number.monthlyCost || 0}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={number.provider} color="default" size="small" />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={(e) => handleMenuOpen(e, number)}
                        size="small"
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* Add/Edit Phone Number Dialog */}
      <Dialog open={showModal} onClose={() => setShowModal(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingNumber ? 'Edit Phone Number' : 'Add Phone Number'}
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phoneNumber}
                onChange={(e) => setFormData({...formData, phoneNumber: e.target.value})}
                required
              />
              <TextField
                fullWidth
                label="Friendly Name"
                value={formData.friendlyName}
                onChange={(e) => setFormData({...formData, friendlyName: e.target.value})}
              />
            </Box>
            
            <TextField
              fullWidth
              label="Monthly Cost"
              type="number"
              value={formData.monthlyCost}
              onChange={(e) => setFormData({...formData, monthlyCost: parseFloat(e.target.value)})}
              sx={{ mb: 2 }}
            />
            
            <Typography variant="h6" gutterBottom>
              Capabilities
            </Typography>
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.capabilities.voice}
                    onChange={(e) => setFormData({
                      ...formData,
                      capabilities: { ...formData.capabilities, voice: e.target.checked }
                    })}
                  />
                }
                label="Voice"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.capabilities.sms}
                    onChange={(e) => setFormData({
                      ...formData,
                      capabilities: { ...formData.capabilities, sms: e.target.checked }
                    })}
                  />
                }
                label="SMS"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.capabilities.mms}
                    onChange={(e) => setFormData({
                      ...formData,
                      capabilities: { ...formData.capabilities, mms: e.target.checked }
                    })}
                  />
                }
                label="MMS"
              />
            </Box>
            
            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={3}
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingNumber ? 'Update' : 'Add'} Phone Number
          </Button>
        </DialogActions>
      </Dialog>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          handleEdit(selectedNumberForMenu);
          handleMenuClose();
        }}>
          <EditIcon sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem 
          onClick={() => {
            handleDelete(selectedNumberForMenu._id);
            handleMenuClose();
          }}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Twilio Import Dialog */}
      <TwilioImportDialog
        open={showTwilioImport}
        onClose={() => setShowTwilioImport(false)}
        onSuccess={handleTwilioImportSuccess}
      />
    </Container>
  );
};

export default PhoneNumbersPage; 